import { Paperclip } from "lucide-react";
import { ContainerBloco, TituloBloco } from "../Common/styles";
import { Container } from "./styles";
import { comum } from "../../../styles/Themes/light";
import { IProposta } from "../../../utility/IProposta";
import { IAnexo } from "../../../utility/IAnexo";
import { useState } from "react";
import { FileUploadInfo } from "./BlocoAdicionaAnexo";
import { useEffect } from "react";
import { uploadFileWithStateManagement } from "./fileUploadHelpers";
import BlocoAdicionaAnexo from "./BlocoAdicionaAnexo";
import BlocoListaAnexos from "./BlocoListaAnexos";
import AbaErro from "../Common/AbaErro";
import AbaAcessoNegado from "../Common/AbaAcessoNegado";
import api from "../../../services/api";

interface AbaAnexosProps {
    abaAtiva: string;
    demanda?: IProposta | null;
    loading?: boolean;
    error?: boolean;
    acessoNegado?: boolean;
    onRetry?: () => void;
    setNAnexos: (nAnexos: number) => void;
    highlightedFileId?: number | null;
}

export default function AbaAnexos({ abaAtiva, demanda, loading, error, acessoNegado, onRetry, setNAnexos, highlightedFileId }: AbaAnexosProps) {

    const [loadingAnexos, setLoadingAnexos] = useState(true);
    const [anexos, setAnexos] = useState<IAnexo[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<FileUploadInfo[]>([]);

    const adicionarAnexo = (anexo: IAnexo) => {
        // Função auxiliar para normalizar nomes de arquivo
        const normalizarNome = (nome: string): string => {
            return nome.toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9.]/g, '');
        };
        
        // Remove card de upload correspondente imediatamente
        setUploadingFiles(prevUploading => {
            // Extrai o nome do arquivo do caminho
            const nomeAnexo = anexo.caminho ? anexo.caminho.split('/').pop() || '' : '';
            const nomeAnexoNormalizado = nomeAnexo ? normalizarNome(nomeAnexo) : '';
            
            return prevUploading.filter(uploadFile => {
                const nomeUploadNormalizado = normalizarNome(uploadFile.file.name);
                // Se os nomes correspondem, remove o card de upload
                return nomeUploadNormalizado !== nomeAnexoNormalizado;
            });
        });
        
        setAnexos(prev => {
            // Verifica se o anexo já existe antes de adicionar (evita duplicação)
            const jaExiste = prev.some(a => a.id_proposta_arquivo === anexo.id_proposta_arquivo);
            if (jaExiste) {
                return prev;
            }
            return [anexo, ...prev];
        });
    }

    useEffect(() => {
        setNAnexos(anexos.length);
    }, [anexos]);

    const cancelUpload = (index: number) => {
        setUploadingFiles(prev => prev.filter((_, idx) => idx !== index));
    };

    const retryUpload = async (index: number) => {
        const fileInfo = uploadingFiles[index];
        if (!fileInfo || fileInfo.status !== 'error') return;

        // Reset status to uploading
        setUploadingFiles(prev => 
            prev.map((f, idx) => 
                idx === index ? { ...f, status: 'uploading' as const, progress: 0, error: undefined } : f
            )
        );

        // Upload the file using shared helper
        await uploadFileWithStateManagement({
            file: fileInfo.file,
            index,
            demandaId: demanda?.id_proposta,
            setUploadingFiles,
            removalDelay: 1000,
            onSuccessCallback: (responseData) => {
                if (responseData?.result) {
                    adicionarAnexo(responseData.result);
                }
            }
        });
    };

    if (abaAtiva !== 'anexos') {
        return null;
    }

    if (acessoNegado) {
        return <AbaAcessoNegado />;
    }

    if (error && onRetry) {
        return <AbaErro mensagem="Não foi possível carregar anexos." onRetry={onRetry} />;
    }

    return (
        <Container>
            <ContainerBloco>
                <TituloBloco>
                    <Paperclip size={16} color={comum.colors.primaryRed400}/>
                    Anexos de Demanda
                </TituloBloco>
                <BlocoAdicionaAnexo 
                    id_proposta={demanda?.id_proposta} 
                    setLoading={setLoadingAnexos}
                    uploadingFiles={uploadingFiles}
                    setUploadingFiles={setUploadingFiles}
                    onAdicionarAnexo={adicionarAnexo}
                    onRecarregarAnexos={() => {
                        // Recarrega a lista de anexos para garantir contador correto
                        setLoadingAnexos(true);
                        api.get(`proposta/${demanda?.id_proposta}/anexos`)
                            .then((response: any) => {
                                const novosAnexos = response.data.result || [];
                                
                                // Função auxiliar para normalizar nomes de arquivo
                                const normalizarNome = (nome: string): string => {
                                    return nome.toLowerCase()
                                        .normalize('NFD')
                                        .replace(/[\u0300-\u036f]/g, '')
                                        .replace(/[^a-z0-9.]/g, '');
                                };
                                
                                // Remove cards de upload que já apareceram na lista recarregada
                                setUploadingFiles(prevUploading => {
                                    return prevUploading.filter(uploadFile => {
                                        const nomeUploadNormalizado = normalizarNome(uploadFile.file.name);
                                        
                                        // Verifica se o arquivo já está na lista de anexos recarregada
                                        const jaEstaNaLista = novosAnexos.some((anexo: IAnexo) => {
                                            // Compara por caminho (extrai nome do caminho)
                                            if (anexo.caminho) {
                                                const nomeDoCaminho = anexo.caminho.split('/').pop() || '';
                                                const nomeCaminhoNormalizado = normalizarNome(nomeDoCaminho);
                                                if (nomeCaminhoNormalizado === nomeUploadNormalizado) {
                                                    return true;
                                                }
                                            }
                                            
                                            return false;
                                        });
                                        
                                        // Se já está na lista, remove o card de upload imediatamente
                                        return !jaEstaNaLista;
                                    });
                                });
                                
                                // Remove duplicados baseado no ID antes de atualizar
                                setAnexos(prevAnexos => {
                                    const idsExistentes = new Set(prevAnexos.map(a => a.id_proposta_arquivo));
                                    const anexosSemDuplicados = novosAnexos.filter((novo: IAnexo) => 
                                        !idsExistentes.has(novo.id_proposta_arquivo)
                                    );
                                    // Retorna apenas os novos anexos do backend (fonte da verdade)
                                    return novosAnexos;
                                });
                                setLoadingAnexos(false);
                            })
                            .catch(() => {
                                setLoadingAnexos(false);
                            });
                    }}
                />
                <BlocoListaAnexos 
                    id_proposta={demanda?.id_proposta} 
                    loading={loadingAnexos} 
                    anexos={anexos}
                    setAnexos={setAnexos}
                    setLoading={setLoadingAnexos}
                    uploadingFiles={uploadingFiles}
                    onRetry={retryUpload}
                    onCancelUpload={cancelUpload}
                    highlightedFileId={highlightedFileId}
                />
            </ContainerBloco>
        </Container>
    );
}
